import { useCallback, useState } from 'react';

import { BOARD_LIMIT, useAddPostsToBoard, useBoards, useCreateBoard } from '@/entities/board';
import type { PostListItem } from '@/entities/post/types';
import { toBoardsErrorMessage } from '@/features/boards/helpers/boards-error-message';

export function useBoardsPosts() {
  const { data: boards = [], isLoading: isBoardsLoading } = useBoards();

  const createBoardMutation = useCreateBoard();
  const addPostsToBoardMutation = useAddPostsToBoard();

  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([]);

  const createList = useCallback(
    async (title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return { ok: false as const, reason: 'Please enter a list name.' };
      if (boards.length >= BOARD_LIMIT) {
        return { ok: false as const, reason: `You can create up to ${BOARD_LIMIT} boards.` };
      }

      try {
        const createdBoard = await createBoardMutation.mutateAsync({ title: trimmed });
        return { ok: true as const, listId: createdBoard.id };
      } catch (error) {
        return {
          ok: false as const,
          reason: toBoardsErrorMessage(error, 'Failed to create board.'),
        };
      }
    },
    [boards.length, createBoardMutation],
  );

  const toggleSelectedPost = useCallback((post: PostListItem) => {
    setSelectedPostIds((prev) => {
      const postId = post._id;
      return prev.includes(postId) ? prev.filter((id) => id !== postId) : [...prev, postId];
    });
  }, []);

  const clearSelectedPosts = useCallback(() => {
    setSelectedPostIds([]);
  }, []);

  const addPostsToLists = useCallback(
    async (listIds: string[], post_ids: string[]): Promise<number> => {
      const uniqueListIds = Array.from(new Set(listIds));

      let result = 0;

      if (uniqueListIds.length === 0 || post_ids.length === 0) return result;

      for (const boardId of uniqueListIds) {
        await addPostsToBoardMutation.mutateAsync({
          boardId,
          post_ids: post_ids,
        });
        result += post_ids.length;
      }

      return result;
    },
    [addPostsToBoardMutation],
  );

  const isMutating = createBoardMutation.isPending || addPostsToBoardMutation.isPending;

  return {
    lists: boards,
    selectedPostIds,
    selectedCount: selectedPostIds.length,
    isLoading: isBoardsLoading || isMutating,
    createList,
    toggleSelectedPost,
    clearSelectedPosts,
    addPostsToLists,
  };
}
